import React from 'react';
import { omit } from 'lodash';
import { Loader } from 'semantic-ui-react';
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
  tableCellClasses,
} from '@mui/material';
import { Deal } from '@upstart/harmony/lib/legos/dealTypes';
import { CreditApp } from './LendingSupport';
import { JsonLink } from './JsonLink';
import './CreditApplications.scss';

const HeaderCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#f9f9f9',
    fontStyle: 'italic',
  },
}));

const renderButton = (text: string, onClick: () => void) => (
  <Button variant="outlined" size="small" onClick={onClick}>
    {text}
  </Button>
);

interface CreditApplicationsProps {
  creditAppList: CreditApp[] | null | undefined;
  deal: Deal | null | undefined;
  fetchDmsDeal: (dealObj: Deal, dmsDealId: string) => () => void;
  fetchStatus: (providerApplicationId: string) => () => void;
  onClick: (data?: object | null) => void;
}

/**
 * This component renders the credit application section of the lending support tool,
 * showing the list of finance applications, responses, and their statuses.
 */
export const CreditApplications: React.FC<CreditApplicationsProps> = ({
  deal,
  creditAppList,
  onClick,
  fetchStatus,
  fetchDmsDeal,
}) => {
  if (deal === null || creditAppList === null) {
    return (
      <div className="lending-support-credit-apps-empty-container">n/a</div>
    );
  }
  if (creditAppList) {
    // Sort the credit applications by creation date in descending order
    const sortedCreditAppList = [...creditAppList].sort((a, b) => {
      if (a.financeApplication.createdAt && b.financeApplication.createdAt) {
        return (
          new Date(b.financeApplication.createdAt).getTime() -
          new Date(a.financeApplication.createdAt).getTime()
        );
      }
      return 0;
    });
    return (
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="credit applications">
          <TableHead>
            <TableRow>
              <HeaderCell>Provider</HeaderCell>
              <HeaderCell>&nbsp;</HeaderCell>
              <HeaderCell>Application ID</HeaderCell>
              <HeaderCell>&nbsp;</HeaderCell>
              <HeaderCell>Application Status</HeaderCell>
              <HeaderCell>DMS Deal</HeaderCell>
              <HeaderCell>Created</HeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedCreditAppList.map(creditApp => (
              <TableRow key={creditApp.financeApplication.id}>
                <TableCell component="th" scope="row">
                  {creditApp.financeApplication.provider}
                </TableCell>
                <TableCell>
                  <JsonLink
                    data={creditApp.financeApplication}
                    text="financeApplication"
                    onClick={() => onClick(creditApp.financeApplication)}
                  />
                </TableCell>
                <TableCell>{creditApp.financeApplication.id}</TableCell>
                <TableCell>
                  {creditApp.financeResponses ? (
                    creditApp.financeResponses.map(financeResponse => (
                      <div key={financeResponse.id}>
                        <JsonLink
                          data={financeResponse}
                          text="financeResponse"
                          isSelected={financeResponse.isSelected}
                          onClick={() =>
                            onClick(omit(financeResponse, ['isSelected']))
                          }
                        />
                      </div>
                    ))
                  ) : (
                    <div>n/a</div>
                  )}
                </TableCell>
                <TableCell>
                  <div>
                    {creditApp.financeApplication.provider === 'upstart' ? (
                      renderButton(
                        'fetch application status',
                        fetchStatus(
                          creditApp.financeApplication.providerApplicationId ??
                            '',
                        ),
                      )
                    ) : (
                      <div>n/a</div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    {creditApp.financeApplication.provider === 'upstart' &&
                    deal?.financeMeta?.dmsDealInfo?.id ? (
                      renderButton(
                        'fetch DMS deal',
                        fetchDmsDeal(deal, deal.financeMeta.dmsDealInfo.id),
                      )
                    ) : (
                      <div>n/a</div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    {creditApp.financeApplication.createdAt
                      ? new Date(
                          creditApp.financeApplication.createdAt,
                        ).toLocaleString()
                      : 'n/a'}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }
  return (
    <div className="lending-support-credit-apps-empty-container">
      <Loader size="small" active={!creditAppList} inline />
    </div>
  );
};
